import { FC, useState } from 'react';

import { orderBy } from 'lodash';

import { useEventQuery } from '@queries/EventQueries';
import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';
import { useCurrentUser } from '@queries/UserQueries';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { UserAvatar } from '@components/layout/UserAvatar/UserAvatar';

import { SuggestedConnectionCard } from '@screens/connnections/SuggestedConnectionsScreen/SuggestedConnectionsScreen';

import { useEventRsvpActions } from '../hooks/useEventRsvpActions';

const RsvpsSection: FC<{
  attendingCount: number;
  interestedCount: number;
  connectionRsvps: any[];
  eventEntertainers?: any[];
  currentUserRsvp: any;
  onMarkAttending: () => void;
}> = ({
  attendingCount = 0,
  interestedCount = 0,
  connectionRsvps = [],
  eventEntertainers = [],
  currentUserRsvp,
  onMarkAttending,
}) => {
  const { currentUser } = useCurrentUser();

  const isCurrentUserAttending = currentUserRsvp?.rsvp === 'yes';
  const isCurrentUserInterested =
    currentUserRsvp?.rsvp !== 'yes' && currentUserRsvp?.eventInterest === 'interested';

  const totalRsvpsCount = attendingCount + interestedCount + eventEntertainers.length;

  const publicRsvpUsers: {
    userId: string;
    name: string;
    avatar: string;
    type: 'entertainer' | 'connection';
  }[] = [
    ...connectionRsvps.map((connection) => ({ ...connection, type: 'connection' })),
    ...orderBy(eventEntertainers, ({ avatar }) => (avatar ? 1 : -1), 'desc').map((entertainer) => ({
      userId: entertainer.userId ?? entertainer._id,
      name: entertainer.title,
      avatar: entertainer.avatar,
      type: 'entertainer',
    })),
  ].slice(0, 3);

  const isCurrentUserShown = isCurrentUserAttending || isCurrentUserInterested;
  const otherUsersRsvpCount =
    totalRsvpsCount - (isCurrentUserShown ? 1 : 0) - publicRsvpUsers.length;

  return (
    <div>
      {totalRsvpsCount > 0 ? (
        <div>
          <div className="d-flex gap-1">
            {isCurrentUserAttending || isCurrentUserInterested ? (
              <div>
                <UserAvatar
                  displayName={currentUser?.privateDisplayName ?? currentUser?.displayName ?? 'You'}
                  avatarUrl={currentUser?.avatar}
                />
              </div>
            ) : (
              <div>
                <Button
                  startIcon={<FontAwesomeIcon faStyle="far" name="circle" />}
                  variant="outlined"
                  color="neutral"
                  style={{ height: 42 }}
                  onClick={onMarkAttending}
                >
                  I'm going!
                </Button>
              </div>
            )}
            {publicRsvpUsers.map((connection) => (
              <div key={connection.userId}>
                <UserAvatar displayName={connection.name} avatarUrl={connection.avatar} />
              </div>
            ))}
            {otherUsersRsvpCount > 0 && (
              <div
                className="px-3"
                style={{
                  height: 42,
                  borderRadius: 500,
                  fontWeight: 400,
                  background: 'rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span>{`+ ${otherUsersRsvpCount}`}</span>
              </div>
            )}
          </div>

          {connectionRsvps.length > 3 && (
            <Button variant="flat" color="neutral" size="sm">
              View All
            </Button>
          )}
        </div>
      ) : (
        <EmptyState
          title="No one has RSVPed yet."
          text="Be the first to RSVP and invite your friends!"
          button={{
            text: "I'm going!",
            onClick: onMarkAttending,
          }}
        />
      )}
    </div>
  );
};

const SuggestedFriendsSection: FC<{ showSkeleton?: boolean; titleOverride?: string }> = ({
  showSkeleton = true,
  titleOverride,
}) => {
  const { connectionsAreReady, suggestedConnections } = useUserConnectionsQuery();

  if (!connectionsAreReady && showSkeleton) {
    return <Skeleton height={120} />;
  }

  if (!suggestedConnections.length) {
    return <></>;
  }

  return (
    <>
      <TitleToolbar text={titleOverride ?? 'Suggested Friends'} size="sm" />
      <HorizontalScroll
        items={[
          <div className="d-flex gap-2 pt-2 pe-4">
            {suggestedConnections.map((suggestedConnection) => (
              <div key={suggestedConnection.connectionUserId}>
                <SuggestedConnectionCard connection={suggestedConnection} />
              </div>
            ))}
          </div>,
        ]}
      />
    </>
  );
};

export const EventRsvpsSection: FC<{
  titleOverride?: string;
  suggestedFriendsTitleOverride?: string;
  eventId: string;
  referredBy: string | undefined;
  onInvite?: () => void;
}> = ({ titleOverride, suggestedFriendsTitleOverride, eventId, referredBy, onInvite }) => {
  const { event } = useEventQuery(eventId);
  const { rsvp, isUpdatingRsvp, handleMarkInterested, handleMarkAttending } = useEventRsvpActions(
    { eventId, eventTitle: event?.title },
    referredBy,
  );
  const { currentUser } = useCurrentUser();
  const [didMarkAttendingUnauthenticated, setDidMarkAttendingUnauthenticated] = useState(false);

  const handleMarkAttendingWrapped = () => {
    if (!currentUser?._id) {
      setDidMarkAttendingUnauthenticated(true);
    }

    handleMarkAttending();
  };

  return (
    <>
      <TitleToolbar
        renderAs="h3"
        className="my-0"
        size="md"
        text={titleOverride ?? `RSVPs`}
        action={
          onInvite && (
            <Button
              className="py-1 px-3"
              size="sm"
              startIcon={<FontAwesomeIcon name="plus" />}
              onClick={onInvite}
            >
              Invite Friends
            </Button>
          )
        }
      />
      <div className="mb-2" style={{ fontSize: 12, opacity: 0.8, fontWeight: 400 }}>
        {[
          `${(event.eventUserResponses?.attendingCount ?? 0) + (event.entertainers?.length ?? 0)} attending`,
          `${event.eventUserResponses?.interestedCount ?? 0} interested`,
        ].join(' • ')}
      </div>
      <div>
        <RsvpsSection
          attendingCount={event.eventUserResponses?.attendingCount}
          interestedCount={event.eventUserResponses?.interestedCount}
          connectionRsvps={event.eventUserResponses?.connections}
          eventEntertainers={event.entertainers}
          currentUserRsvp={rsvp}
          onMarkAttending={handleMarkAttendingWrapped}
        />
      </div>

      {didMarkAttendingUnauthenticated && currentUser?._id && (
        <SuggestedFriendsSection
          showSkeleton={false}
          titleOverride={suggestedFriendsTitleOverride}
        />
      )}
    </>
  );
};
